/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import { Card } from "react-bootstrap";
import { BsCheckCircleFill } from "react-icons/bs";
import "./style.css";
import { getAuditLevelId } from "../../../utils";
import AuditPDFPreviewModal from "../../../Components/GeneratePDF";
import { MdSimCardDownload } from "react-icons/md";

interface AuditInfoCardProps {
  code: string | null;
  type: string | null;
  status: string | null;
  completion: string | null;
  auditor: string;
  name: string;
  level: string;
  auditMode: string;
  turnAroundDays: number;
  selected: boolean;
  shouldShrink: boolean;
  id: any;
  onClick?: () => void;
  organizations: { organizationName: string }[];
  endDate: string | null;
  startDate: string | null;
}

const AuditInfoCard: React.FC<AuditInfoCardProps> = ({
  code,
  endDate,
  startDate,
  organizations,
  status,
  completion,
  turnAroundDays,
  selected,
  level,
  shouldShrink,
  onClick,
  id,
}) => {
  const [slideIndex, setSlideIndex] = useState(0);
  const [showCondensed, setShowCondensed] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [visibleOrgs, setVisibleOrgs] = useState<string[]>([]);
  const [hiddenOrgs, setHiddenOrgs] = useState<string[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const containerRef = useRef<HTMLTableCellElement>(null);

  const orgNames = organizations?.length > 0
    ? organizations.map(org => org.organizationName).sort((a, b) => a.localeCompare(b))
    : [];

  const slides = [
    {
      left: <div>Audit Id: {code}</div>,
    },
    {
      left: (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
          Organisations: {orgNames.length > 0 ? orgNames.join(", ") : "-"}
        </div>
      ),
    },
    {
      left: <div>Status: {status ? status : "-"}</div>,
    },
    {
      left: <div>Completion: {completion ? completion : "-"}</div>,
    },
  ];

  // Calculate visible organizations based on available width
  const calculateVisibleOrgs = () => {
    if (!containerRef.current || orgNames.length === 0) {
      setVisibleOrgs(orgNames);
      setHiddenOrgs([]);
      return;
    }

    const containerWidth = containerRef.current.offsetWidth;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    // Get computed font from the element
    const computedStyle = window.getComputedStyle(containerRef.current);
    context.font = computedStyle.font || '14px sans-serif';

    let currentWidth = 0;
    let visible: string[] = [];
    let hidden: string[] = [];
    const dotWidth = 50; // Reduced width for "... More" text to show more organizations

    for (let i = 0; i < orgNames.length; i++) {
      const org = orgNames[i];
      const separator = i === 0 ? '' : ', ';
      const textWidth = context.measureText(separator + org).width;

      // Check if we need to show the overflow indicator
      if (i < orgNames.length - 1 && currentWidth + textWidth + dotWidth > containerWidth - 10) {
        hidden = orgNames.slice(i);
        break;
      } else if (currentWidth + textWidth > containerWidth - 10) {
        hidden = orgNames.slice(i);
        break;
      } else {
        currentWidth += textWidth;
        visible.push(org);
      }
    }

    setVisibleOrgs(visible);
    setHiddenOrgs(hidden);
  };

  useEffect(() => {
    calculateVisibleOrgs();
    window.addEventListener('resize', calculateVisibleOrgs);
    return () => window.removeEventListener('resize', calculateVisibleOrgs);
  }, [organizations]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (shouldShrink && !showCondensed) {
      timer = setTimeout(() => setShowCondensed(true), 1000);
    }
    if (!shouldShrink) {
      setShowCondensed(false);
      setSlideIndex(0);
    }
    return () => clearTimeout(timer);
  }, [shouldShrink]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (showCondensed && selected) {
      interval = setInterval(() => {
        setSlideIndex((prev) => (prev + 1) % slides.length);
      }, 2500);
    }
    return () => clearInterval(interval);
  }, [showCondensed, selected]);

  const isDark = selected && showCondensed;
  const cardBg = isDark ? "#0b1e51" : "#fff";
  const textColor = isDark ? "#fff" : "#212529";
  const dotColor = isDark ? "#fff" : "#666";

  return (
    <Card
      className={`auditcard audit-info-card shadow-sm ${shouldShrink ? "shrinking" : ""} ${selected && !showCondensed ? "selectedCard" : ""} align-items-stretch `}
      style={{ backgroundColor: cardBg, color: textColor }}
      onClick={onClick}
      data-testid="audit-card"
    >
      <Card.Body
        className={`d-flex justify-content-between align-items-center flex-column ${isDark ? "text-white" : ""
          }`}
      >
        {/* Full content view */}
        {!showCondensed && (
          <div className="w-100">
            <div className="d-flex justify-content-between mb-3">
              <strong className="h6 fw-bold">
                Audit : {code ? code : "-"}
              </strong>
              {selected && (
                <div className="text-selected d-flex">
                  <BsCheckCircleFill className="me-1 checkCircle" />
                  Selected
                </div>
              )}
            </div>
            <table className="table table-borderless table-sm mb-0">
              {/* This thead has been added to fix a SonarQube vulnerability. 
                        As per the SonarQube syntax no table should exist without the thead and row scope Since it is visually hidden, it will not affect the screen.  */}
              <thead className="visually-hidden">
                <tr>
                  <th>Column 1</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="label">Organisations:</td>
                  <td className="value position-relative" ref={containerRef}>
                    {orgNames.length > 0 ? (
                      <>
                        {visibleOrgs.join(", ")}
                        {hiddenOrgs.length > 0 && (
                          <span className="position-relative d-inline-block">
                            <span
                              className="text-primary cursor-pointer ms-1"
                              onMouseEnter={() => setShowTooltip(true)}
                              onMouseLeave={() => setShowTooltip(false)}
                            >
                              ... More
                            </span>
                            {showTooltip && (
                              <div
                                className="position-absolute bg-light text-dark rounded px-3 py-2 border show-tooltip"
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                              >

                                {orgNames.join(', ')}
                                <span
                                  className="position-absolute bg-light border-top border-start arrow-tooltip"
                                />
                              </div>
                            )}
                          </span>
                        )}
                      </>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="label">Audit Level:</td>
                  <td className="value">
                    {level ? `${level} (${getAuditLevelId(level)})` : "-"}
                  </td>
                </tr>
                <tr>
                  <td className="label">Response Time:</td>
                  <td className="value">
                    {turnAroundDays ? turnAroundDays : "-"} Days
                  </td>
                </tr>
                <tr>
                  <td className="label">Duration:</td>
                  <td className="value">
                    {startDate?.split("-")?.reverse()?.join("/")}  -  {endDate?.split("-")?.reverse()?.join("/")}
                  </td>
                </tr>
                <tr>
                  <td className="label">Status:</td>
                  <td className="value">{status ? status : "-"}</td>
                </tr>
                <tr>
                  <td className="label">Completion:</td>
                  <td className="value">{completion ? completion : "-"}</td>
                </tr>
              </tbody>
            </table>
            <MdSimCardDownload
              className="float-end"
              title="Download Audit Report"
              onClick={() => setShowModal(true)}
            ></MdSimCardDownload>
            <AuditPDFPreviewModal
              show={showModal}
              id={id}
              status={status}
              onClose={() => setShowModal(false)}
            />
          </div>
        )}

        {/* Condensed slide content view */}
        {showCondensed && (
          <div className="d-flex w-100 align-items-center progressDotheight">
            {/* Slide Text Block */}
            <div className="flex-grow-1 d-flex gap-4 overflow-hidden">
              <div className="CondensedView">{slides[slideIndex].left}</div>
            </div>
            
            {/* Dot indicators aligned right */}
            <div className="ms-auto d-flex flex-column align-items-center justify-content-center ps-2">
              {showCondensed &&
                selected &&
                slides.map((_, i) => (
                  <div
                    className="slides"
                    key={i}
                    style={{
                      backgroundColor: i === slideIndex ? dotColor : "#ccc",
                    }}
                  />
                ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default AuditInfoCard;